import { withTranslation } from "react-i18next";
import WithRouter from "../../../../components/Common/WithRouter";
import React, { useRef, useState } from "react";
import { ConfirmPopup } from 'primereact/confirmpopup';
import StateLessGrid from "../../../../components/Shared/StateLessGrid";
import QuickCopyDropdown from "../../../../components/Shared/QuickCopyDropdown";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { InputField } from "../../../../components/Shared/InputField";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { find, get, isEmpty, last, groupBy, orderBy, map } from "lodash";
import InProgressOrderDamageCaseDetail from "./InProgressOrderDamageCaseDetail";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import { request } from "../../../../utils/Request";
import { ButtonGroup } from "primereact/buttongroup";
import InProgressOrderDamageCaseExcel from "./InProgressOrderDamageCaseExcel";
import InProgressOrderDamageCaseFilter from "./InProgressOrderDamageCaseFilter";
import HasAccess from "../../../../utils/HasAccess";
import { utcDateToLocalDate, formatDate } from "../../../../utils/Helper";
import OrderDamageCaseActionBadge from "../../../../components/Common/Display/OrderDamageCaseActionBadge";
import OrderDamageCaseLabelBadge from "../../../../components/Common/Display/OrderDamageCaseLabelBadge";
import OrderDamageCaseStatus from "../../../../components/Common/Display/OrderDamageCaseStatus";
import OpenOrderDamageCaseUpdate from "../../orderDamageCasesOpen/partials/OpenOrderDamageCaseUpdate";
import OrderDamageCaseCodeUpdate from "./OrderDamageCaseCodeUpdate";
import OrderDamageCaseCalculationUpdate from "./OrderDamageCaseCalculationUpdate";
import OpenOrderDamageCaseCostUpdate from "../../orderDamageCasesOpen/partials/OpenOrderDamageCaseCostUpdate";
import { Chatbox } from "../../../../components/Chat";

const InProgressOrderDamageCaseList = (props: any) => {
    const { t, moduleState, toastify } = props;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [operation, setOperation] = useState<any>(searchParams.get('operation') ?? null);
    const [operationId, setOperationId] = useState<any>(searchParams.get('operationId') ?? null);
    const [filterPanel, setFilterPanel] = useState<boolean>(false);
    const [excelPanel, setExcelPanel] = useState<boolean>(false);
    const [updatePanel, setUpdatePanel] = useState<string|null>(null);
    const [selectedCaseNumber, setSelectedCaseNumber] = useState<string>('');
    const [codeUpdatePanel, setCodeUpdatePanel] = useState<boolean>(false);
    const [calculationUpdatePanel, setCalculationUpdatePanel] = useState<boolean>(false);
    const [costUpdatePanel, setCostUpdatePanel] = useState<boolean>(false);
    const [selectedDamageCase, setSelectedDamageCase] = useState<any>(null);
    const [initSearchFormState, setInitSearchFormState] = useState<any>({
        search: moduleState.search ?? null
    });
    const [total, setTotal] = useState(0);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const tableRef: any = useRef();
    const [clickCount, setClickCount] = useState(0);
    const [visibleSpecialAction, setVisibleSpecialAction] = useState(false);
    const [disableSpecialAction, setDisableSpecialAction] = useState(false);
    const [chatboxOpen, setChatboxOpen] = useState<boolean>(false);
    const [selectedDamageCode, setSelectedDamageCode] = useState<any>(null);
    const [selectedDamageCaseForChat, setSelectedDamageCaseForChat] = useState<any>(null);

    const onUpdateClick = (row: any) => {
        setUpdatePanel(get(row, ['id']));
        setSelectedCaseNumber(get(row, ['case_number']) || '');
    };

    const onCodeUpdateClick = (damageCase: any) => {
        setSelectedDamageCase(damageCase);
        setCodeUpdatePanel(true);
    };

    const onCalculationUpdateClick = (damageCase: any) => {
        setSelectedDamageCase(damageCase);
        setCalculationUpdatePanel(true);
    };

    const onCostUpdateClick = (damageCase: any) => {
        setSelectedDamageCase(damageCase);
        setCostUpdatePanel(true);
    };

    const onSyncDamageCaseClick = async (id: any) => {
        try {
            const response = await request({
                method: 'POST',
                url: `/suppliers/provider/order/damage/cases/${ id }/sync`,
            });

            toastify(t("record updated", { item: t('damage case') }), "info");
            refreshTable();
        } catch (e: any) {
            if ( e.status === 422 ) {
                toastify(last(e.data.errors.sync), "error");
                return;
            }

            if ( e.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const refreshTable = () => {
        if ( tableRef.current ) {
            tableRef.current.onRefresh();
        }
    };

    const columns = [
        {
            field: 'vin_number',
            title: t('VIN'),
            sorting: true,
            render: (_row: any) => (
                <div>
                    <div>{ get(_row, ['order', 'vin_number']) || '-' }</div>
                    { get(_row, ['order', 'brand', 'title']) && (
                        <div className="text-muted small">{ get(_row, ['order', 'brand', 'title']) }</div>
                    ) }
                    { get(_row, ['order', 'model', 'title']) && (
                        <div className="text-muted small">{ get(_row, ['order', 'model', 'title']) }</div>
                    ) }
                </div>
            ),
        },
        {
            field: 'case_number',
            title: t('case number'),
            sorting: true,
            render: (_row: any) => {
                const actions = get(_row, ['actions']) || [];
                const allLabels = ( get(_row, ['labels']) || [] ).filter((label: any) => !get(label, ['label_expired_at']));

                // Group labels by label_added_for and get the most recent one from each group
                const groupedLabels = groupBy(allLabels, 'label_added_for');
                const uniqueLabels = map(groupedLabels, (group) => {
                    return orderBy(group, ['created_at'], ['desc'])[0];
                });

                const status = get(_row, ['status']);

                return (
                    <div>
                        <div className="mb-1">{ get(_row, ['case_number']) || '-' }</div>
                        { status && (
                            <div className="mb-1">
                                <OrderDamageCaseStatus status={ status } />
                            </div>
                        ) }
                        { actions.length > 0 && (
                            <div className="d-flex flex-wrap gap-1 mb-1">
                                { actions.filter((item: any) => item.type != 'update-responsibility').map((item: any, index: number) => (
                                    <OrderDamageCaseActionBadge
                                        key={ index }
                                        type={ get(item, ['type']) }
                                    />
                                )) }
                            </div>
                        ) }
                        { uniqueLabels.length > 0 && (
                            <div className="d-flex flex-wrap gap-1">
                                { uniqueLabels.map((item: any, index: number) => (
                                    <Tooltip key={ index }
                                             tooltip={ get(item, ['comment']) || get(item, ['label_added_for']) || '' }>
                                        <span className={"text-capitalize"}>
                                            <OrderDamageCaseLabelBadge
                                                labelFor={ get(item, ['label_added_for']) }
                                            />
                                        </span>
                                    </Tooltip>
                                )) }
                            </div>
                        ) }
                    </div>
                );
            }
        },
        {
            field: 'supplier_carrier',
            title: t('carrier'),
            sorting: true,
        },
        {
            field: 'provider',
            title: t('provider'),
            sorting: false,
            render: (_row: any) => {
                const provider = find(props.dropdowns?.providers || [], { id: get(_row, ['source_type']) });
                return get(provider, ['label'], get(_row, ['source_type']) || '-');
            },
        },
        {
            field: 'comments_count',
            title: t('no. of comments'),
            sorting: false,
        },
        {
            field: 'gate_in',
            title: t('gate in'),
            sorting: true,
            render: (_row: any) => {
                return get(_row, ['gate_in']) ? utcDateToLocalDate(get(_row, ['gate_in'])) : '-';
            }
        },
        {
            field: 'damage_codes',
            title: t('damage codes'),
            sorting: false,
            render: (_row: any) => {
                const codes = get(_row, ['damage_codes']) || [];

                if ( codes.length === 0 ) {
                    return <div>-</div>;
                }

                return (
                    <div>
                        { codes.map((item: any, index: number) => {
                            const emilFreyId = get(item, ['emil_frey_id']);
                            const wasRejected = get(item, ['was_rejected']);
                            const isEmilFreyCase = get(_row, ['source_type']) === 'emil_frey';

                            return (
                                <div key={ index } className="mb-1 d-flex align-items-center gap-2">
                                    { (isEmilFreyCase && emilFreyId && wasRejected) && (
                                        <Tooltip tooltip={ t('open chat') }>
                                            <a
                                                className="cursor-pointer"
                                                onClick={ () => {
                                                    setSelectedDamageCode(item);
                                                    setSelectedDamageCaseForChat(_row);
                                                    setChatboxOpen(true);
                                                } }
                                                style={{ color: '#007bff' }}
                                            >
                                                <i className="fas fa-comment" />
                                            </a>
                                        </Tooltip>
                                    ) }
                                    <div>
                                        <span className="fw-bold">{ get(item, ['code']) }</span>
                                        { get(item, ['description']) && (
                                            <span> - { get(item, ['description']) }</span>
                                        ) }
                                    </div>
                                </div>
                            );
                        }) }
                    </div>
                );
            }
        },
        {
            field: 'responsible',
            title: t('responsible'),
            sorting: false,
            render: (_row: any) => {
                const codes = get(_row, ['damage_codes']) || [];
                if (codes.length === 0) {
                    return <div>-</div>;
                }

                const responsibilityNames = codes
                    .map((code: any) => get(code, ['responsibility_name']))
                    .filter((name: any) => name && name.trim() !== '');

                if (responsibilityNames.length === 0) {
                    return <div>-</div>;
                }

                return (
                    <div>
                        {responsibilityNames.map((name: string, index: number) => (
                            <div key={index} className="mb-1">
                                {name}
                            </div>
                        ))}
                    </div>
                );
            }
        },
        {
            field: 'cost',
            title: t('cost'),
            sorting: false,
            render: (_row: any) => {
                const cost = get(_row, ['cost']);
                return cost ? <div>{cost}</div> : <div>-</div>;
            }
        },
        {
            field: 'damage_date',
            title: t('damage date'),
            sorting: true,
            render: (_row: any) => {
                return get(_row, ['damage_date']) ? formatDate(get(_row, ['damage_date'])) : '-';
            }
        },
        {
            field: 'damage_code_status',
            title: t('status'),
            sorting: false,
            render: (_row: any) => {
                const damageCodes = get(_row, ['damage_codes']) || [];
                const statuses = damageCodes
                .map((dc: any) => get(dc, ['carrier_status']))
                .filter((status: any) => status);

                if ( statuses.length === 0 ) {
                    return <div>-</div>;
                }

                return (
                    <div>
                        { statuses.map((status: any, index: number) => (
                            <div key={ index }>
                                { index + 1 }. { status }
                            </div>
                        )) }
                    </div>
                );
            }
        },
        {
            field: 'created_at',
            title: t('created at'),
            sorting: true,
            render: (_row: any) => utcDateToLocalDate(get(_row, ['created_at']))
        },
        {
            field: 'action',
            title: t('action'),
            sorting: false,
            align: 'right',
            render: (_row: any) => {
                const damageCodes = get(_row, ['damage_codes']) || [];
                const hasPendingOrRejectedCodes = damageCodes.some((code: any) => {
                    const status = get(code, ['carrier_status']);
                    return status === 'pending' || status === 'rejected';
                });

                const calculations = get(_row, ['calculations']) || [];
                const hasPendingCalculations = calculations.some((c: any) => get(c, ['status']) === 'pending');
                const canUpdateCalculations = !hasPendingOrRejectedCodes && hasPendingCalculations;

                return (
                    <>
                        { ( get(_row, ['emil_frey_id']) ) &&
                          <>
                              <Tooltip tooltip={ t('upload document') }>
                                  <a onClick={ () => onUpdateClick(_row) }
                                     className={ "cursor-pointer ms-1" }>
                                      <i className="fas fa-file-upload" />
                                  </a>
                              </Tooltip>
                              <Tooltip tooltip={ t('sync damage case') }>
                                  <a onClick={ () => onSyncDamageCaseClick(get(_row, ['id'])) }
                                     className={ "cursor-pointer ms-1" }>
                                      <i className="fas fa-sync-alt" />
                                  </a>
                              </Tooltip>
                          </>
                        }
                        { hasPendingOrRejectedCodes && (
                            <Tooltip tooltip={ t('update damage codes') }>
                                <a
                                    className={ "cursor-pointer ms-1" }
                                    onClick={ () => onCodeUpdateClick(_row) }>
                                    <i className="fas fa-code" />
                                </a>
                            </Tooltip>
                        ) }
                        { canUpdateCalculations && (
                            <Tooltip tooltip={ t('update calculations') }>
                                <a
                                    className={ "cursor-pointer ms-1" }
                                    onClick={ () => onCalculationUpdateClick(_row) }>
                                    <i className="fas fa-calculator" />
                                </a>
                            </Tooltip>
                        ) }
                        <Tooltip tooltip={ t('update cost') }>
                            <a onClick={ () => onCostUpdateClick(_row) }
                               className={ "cursor-pointer ms-1" }>
                                <i className="fas fa-dollar-sign" />
                            </a>
                        </Tooltip>
                        <HasAccess hasAllAccess={ ['orderdamagecasesview'] }>
                            <Tooltip tooltip={ t('detail of item') }>
                                <a
                                    className={ "cursor-pointer ms-2" }
                                    onClick={ () => onOperationClick('detail', get(_row, ['id'])) }>
                                    <i className="fas fa-eye" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                    </>
                );
            }
        },
    ];

    const onOperationClick = (operationToBePerformed: any = 'add', id: any = null) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('operation', operationToBePerformed);
        searchParams.set('operationId', id);

        if ( isEmpty(operationToBePerformed) ) {
            searchParams.delete('operation');
        }

        if ( isEmpty(id) ) {
            searchParams.delete('operationId');
        }

        navigate({
            pathname: location.pathname,
            search: searchParams.toString(),
        }, { replace: true });
        setOperationId(id);
        setOperation(operationToBePerformed);
    };

    const onCloseOperation = (reset: any = true) => {
        onOperationClick(null, null);
        onReset(reset);
        focusOnSearch();
    };

    const onReset = (reset: any = true) => {
        const mStates = { ...moduleState };

        if ( reset ) {
            mStates.filters = [];
            mStates.page = 1;
            mStates.search = null;
        }

        props.setQuery(mStates);
    };

    const onSearch = ({ search }: any) => {
        props.setQuery({
            ...moduleState,
            search: search
        });
    };

    const onFilterClick = () => {
        setFilterPanel(true);
    };

    const onExcelClick = () => {
        setExcelPanel(true);
    };

    const focusOnSearch = () => {
        const searchInput: HTMLInputElement | null = document.querySelector('input[name="search"]');
        if ( searchInput ) {
            searchInput.focus();
        }
    };

    const handleHeaderClick = () => {
        const newCount = clickCount + 1;
        setClickCount(newCount);

        if ( newCount >= 5 && !visibleSpecialAction ) {
            setVisibleSpecialAction(true);
        }
    };

    const onSyncAllDamageCasesClick = async () => {
        try {
            const response = await request({
                method: 'POST',
                url: `/suppliers/provider/order/damage/cases/sync`,
                data: {
                    type: "in-progress"
                }
            });

            toastify(t("request received to sync all in-progress damage cases"), "info");
        } catch (e: any) {
            toastify(t("server error"), "error");
        } finally {
            setDisableSpecialAction(true);
        }
    };

    return (
        <div>
            <div className={ `block-viewer` }>
                <div className="block-section">
                    <div className="block-header">
                        <span className="block-title">
                            <h5 className={ "mb-0" }>
                                <span onClick={ handleHeaderClick }>
                                    { t('in-progress damage cases') } ({ total })
                                </span>
                            </h5>
                        </span>
                        <div className="block-actions">
                            {
                                operation == 'detail' &&
                              <div>
                                  <Button type={ "button" }
                                          tooltip={ t("operation cancel") } className={ "ms-2" }
                                          size={ "small" } severity={ "warning" }
                                          tooltipOptions={ { position: 'top' } } onClick={ () => onCloseOperation(false) }>
                                      <i className="pi pi-arrow-left me-2" /> { t("cancel") }
                                  </Button>
                              </div>
                            }
                        </div>
                    </div>
                    <div className="block-content">
                        <Row className={ `p-2 ${ operation === 'detail' ? 'position-minus' : '' }` }>
                            <Col
                                sm={ 12 } md={ 6 } className={ "mb-2" }>
                                <Formik
                                    enableReinitialize={ true }
                                    initialValues={ initSearchFormState }
                                    onSubmit={ onSearch }
                                >
                                    { (props: FormikProps<any>) => {
                                        return (
                                            <Form onSubmit={ props.handleSubmit }>
                                                <Col sm={ 12 } md={ 8 }>
                                                    <Field component={ InputField } withoutLabel={ true }
                                                           autoFocus={ true }
                                                           name="search"
                                                           formGroupClass={ "mb-0" }
                                                           placeholder={ t("search by case number") } />
                                                </Col>
                                            </Form>
                                        );
                                    } }
                                </Formik>
                            </Col>

                            <Col sm={ 12 } md={ 6 } className={ "text-right mb-2" }>
                                <div className="d-inline-block me-2 align-middle text-left">
                                    <QuickCopyDropdown
                                        columns={ columns }
                                        tableName={ "supplierInProgressOrderDamageCasesList" }
                                        selectedRows={ selectedRows }
                                        trackBy={ "id" }
                                        // @ts-ignore
                                        entities={ tableRef.current?.getEntities?.() || [] }
                                    />
                                </div>
                                <ButtonGroup>
                                    <Button icon="pi pi-filter-fill" size={ "small" } outlined tooltip={ t('filter') }
                                            onClick={ onFilterClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button icon="pi pi-file-excel" size={ "small" } outlined tooltip={ t('excel') }
                                            onClick={ onExcelClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button onClick={ onReset } icon="pi pi-refresh" size={ "small" } outlined
                                            tooltip={ t('reset') }
                                            tooltipOptions={ { position: 'top' } } />
                                    {
                                        ( visibleSpecialAction && !disableSpecialAction ) &&
                                      <Button onClick={ onSyncAllDamageCasesClick } icon="pi pi-sync" size={ "small" }
                                              outlined
                                              severity={ "danger" }
                                              tooltip={ t('sync all damage cases from emil frey') }
                                              tooltipOptions={ { position: 'top' } } />
                                    }
                                </ButtonGroup>
                            </Col>

                            <Col sm={ 12 }>
                                <StateLessGrid
                                    size={ "normal" }
                                    url={ '/suppliers/order/damage/cases/in-progress' }
                                    name={ "supplierInProgressOrderDamageCasesList" }
                                    columns={ columns }
                                    moduleState={ moduleState }
                                    setQuery={ props.setQuery }
                                    ref={ tableRef }
                                    itemSize={ 45 }
                                    setTotal={ setTotal }
                                    stickyHeader={ true }
                                    showQuickCopy={ false }
                                    onRowSelection={ setSelectedRows }
                                    selectedRows={ selectedRows }
                                />
                            </Col>
                        </Row>

                        {
                            (operation === 'detail' && !isEmpty(operationId)) &&
                            <>
                                <InProgressOrderDamageCaseDetail
                                  setQuery={ props.setQuery }
                                  toastify={ props.toastify }
                                  dropdowns={ props.dropdowns }
                                  params={ { operation, operationId } }
                                  onClose={ onCloseOperation }
                                />
                            </>
                        }

                        <InProgressOrderDamageCaseFilter
                            moduleState={ moduleState }
                            setQuery={ props.setQuery }
                            toastify={ props.toastify }
                            dropdowns={ props.dropdowns }
                            visibility={ filterPanel }
                            onClose={ () => {
                                setFilterPanel(false);
                                focusOnSearch();
                            } }
                        />

                        <InProgressOrderDamageCaseExcel
                            moduleState={ moduleState }
                            setQuery={ props.setQuery }
                            toastify={ props.toastify }
                            visibility={ excelPanel }
                            onClose={ () => {
                                setExcelPanel(false);
                                focusOnSearch();
                            } }
                        />

                        <OpenOrderDamageCaseUpdate
                            visible={ updatePanel }
                            dropdowns={ props.dropdowns }
                            toastify={ props.toastify }
                            caseNumber={ selectedCaseNumber }
                            onClose={ () => {
                                setUpdatePanel(null);
                                setSelectedCaseNumber('');
                                refreshTable();
                            } }
                        />

                        <OrderDamageCaseCodeUpdate
                            visible={ codeUpdatePanel }
                            damageCase={ selectedDamageCase }
                            toastify={ props.toastify }
                            onClose={ () => {
                                setCodeUpdatePanel(false);
                                setSelectedDamageCase(null);
                                refreshTable();
                            } }
                        />

                        <OrderDamageCaseCalculationUpdate
                            visible={ calculationUpdatePanel }
                            damageCase={ selectedDamageCase }
                            toastify={ props.toastify }
                            onClose={ () => {
                                setCalculationUpdatePanel(false);
                                setSelectedDamageCase(null);
                                refreshTable();
                            } }
                        />

                        <OpenOrderDamageCaseCostUpdate
                            visible={ costUpdatePanel }
                            damageCase={ selectedDamageCase }
                            toastify={ props.toastify }
                            status="in-progress"
                            onClose={ () => {
                                setCostUpdatePanel(false);
                                setSelectedDamageCase(null);
                                refreshTable();
                            } }
                        />

                        { selectedDamageCode && selectedDamageCaseForChat && get(selectedDamageCaseForChat, ['source_type']) === 'emil_frey' && get(props.dropdowns, ['providerUser']) && get(props.dropdowns, ['providerUser', 'id']) && (
                            <Chatbox
                                isOpen={ chatboxOpen }
                                onClose={ () => {
                                    setChatboxOpen(false);
                                    setSelectedDamageCode(null);
                                    setSelectedDamageCaseForChat(null);
                                } }
                                participants={ get(selectedDamageCaseForChat, ['participants']) }
                                damageCase={ {
                                    id: get(selectedDamageCaseForChat, ['id']),
                                    emil_frey_id: get(selectedDamageCode, ['emil_frey_id']),
                                    source_type: get(selectedDamageCaseForChat, ['source_type']),
                                    case_number: get(selectedDamageCaseForChat, ['case_number']),
                                    damage_code: get(selectedDamageCode, ['code']),
                                    damage_code_description: get(selectedDamageCode, ['description']),
                                    status: get(selectedDamageCaseForChat, ['status']),
                                } }
                                masterUser={ get(props.dropdowns, ['providerUser']) }
                                allDamageCodes={ get(selectedDamageCaseForChat, ['damage_codes']) || [] }
                                onChangeDamageCode={ (newDamageCode: any) => {
                                    // Update selected damage code to reload chat with new code
                                    setSelectedDamageCode(newDamageCode);
                                } }
                            />
                        ) }

                        <ConfirmPopup />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(WithRouter(InProgressOrderDamageCaseList));

